import * as React from "react";
import {useEffect, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, Checkbox,
    Chip, CircularProgress, Fab,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper, TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Add, Download, Edit, Search} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {PaginatedResult} from "../../../models/default/paginated-result";
import TableActionComponent from "../../../components/table/table-action.component";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import {
    PoMonitoring,
    PoMonitoringDashboard,
    PoMonitoringStatusList
} from "../../../models/basic/po-monitoring";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import AddProjectComponent from "./add-project.component";
import {Project} from "../../../models/basic/project";
import IconButton from "@mui/material/IconButton";
import PoMonitoringStatusComponent from "./po-monitoring-status.component";
import {sortListObj} from "../../../utils/list-utils";
import AddCategoryComponent from "./add-category.component";
import {Category} from "../../../models/basic/category";
import {Field, Form, Formik} from "formik";
import Select from "@mui/material/Select";
import {SelectItem} from "../../../models/basic/select-item";
import InfoCard from "../../../components/info-card/info-card.component";
import newpo from "../../../../assets/images/new-po.svg";
import partialdelivered from "../../../../assets/images/partial-delivered-po.svg";
import deliveredpo from "../../../../assets/images/delivered-po.svg";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {PoMonitoringFilter, PoMonitoringService} from "../../../services/po-monitoring-service";
import {AbstractService} from "../../../services/abstract-service";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {enUS, ptBR} from "date-fns/locale";
import {ConvertDateToString} from "../../../components/date/date-timeFormat";
import AddClientComponent from "./add-client.component";

const PoMonitoringComponent = () => {
    const service = new PoMonitoringService();
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const url = "/general/po-monitoring/";
    const navigate = useNavigate();

    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<PoMonitoringFilter>(
        { text: "", status: "", client: "", ordering: "", start: "", end: "", is_uncategorized: ""},
        ["text", "status", "client", "ordering", "start", "end", "is_uncategorized"]
    );
    const [poMonitoringList, setPoMonitoringList] = React.useState<PaginatedResult<PoMonitoring>>();
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<PoMonitoring>(poMonitoringList);
    const expand = ["client", "category", "projects"];

    const [openProjectsDialog, setOpenProjectsDialog] = React.useState(false);
    const [openCategoryDialog, setOpenCategoryDialog] = React.useState(false);
    const [openClientDialog, setOpenClientDialog] = React.useState(false);
    const [idPoMonitoring, setIdPoMonitoring] = React.useState(0);
    const [projectsPoMonitoring, setProjectsPoMonitoring] = React.useState<Project[]>([]);
    const [categoryPoMonitoring, setCategoryPoMonitoring] = React.useState<Category | undefined>(undefined);
    const [clientPoMonitoring, setClientPoMonitoring] = React.useState<Client | undefined>(undefined);

    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);
    const [statusList, setStatusList] = React.useState(PoMonitoringStatusList);
    const [lastUpdated, setLastUpdated] = React.useState<string>('');
    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');
    const [dataSourceDashboard, setDataSourceDashboard] = React.useState<PoMonitoringDashboard | undefined>(undefined);

    const [loading, setLoading] = React.useState(false);
    const [loadingDownload, setLoadingDownload] = React.useState(false);

    const [invalidDate, setInvalidDate] = useState<boolean>(false);
    const locale = translate.language == "pt" ? ptBR : enUS;

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
        loadClients();
        loadDashboard();
        loadLastUpdated();

        setStartDate(filters.start ? new Date(filters.start + "T12:00:00") : null);
        setEndDate(filters.end ? new Date(filters.end + "T12:00:00") : null);

    }, []);

    const handleOpenEdit = (row: PoMonitoring) => {
        const projects = row.projects.map((project) => {
            return project.project;
        });
        setProjectsPoMonitoring(projects || []);
        setIdPoMonitoring(row.id);
        setOpenProjectsDialog(true);
    }

    const handleOpenCategoryEdit = (row: PoMonitoring) => {
        setCategoryPoMonitoring(row.category);
        setIdPoMonitoring(row.id);
        setOpenCategoryDialog(true);
    }

    const handleOpenClientEdit = (row: PoMonitoring) => {
        setClientPoMonitoring(row.client);
        setIdPoMonitoring(row.id);
        setOpenClientDialog(true);
    }

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            setDataSourceClient(sortListObj(response.data, "name"));
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadDashboard = () => {
        service.getDashboard(filters, pagination).then(response => {
            setDataSourceDashboard(response.data);
        });
    }

    const loadLastUpdated = () => {
        service.getLastUpdated().then(response => {

            if (!response.data.last_updated || response.data.last_updated === '-') return "-";

            const dateStr = response.data.last_updated.endsWith('Z')
                ? response.data.last_updated
                : response.data.last_updated + 'Z';

            const date = new Date(dateStr);

            setLastUpdated(date.toLocaleString([], {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                dateStyle: 'short',
                timeStyle: 'medium'
            }));
        }).catch((error: any) => {
            setLastUpdated(error.response.data.detail)
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.readPaginated(filters, expand, pagination).then(response => {
            setPoMonitoringList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });

        restartIndex && resetPage();
        loadDashboard();
    };

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);

    const handleDelete = (row: PoMonitoring) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };
  
    const export_xlsx = () => {
        setLoadingDownload(true);
        service.download(filters, 'export_xlsx').then(response => {
            service.downloadFile(response);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoadingDownload(false);
        });
    }

    const customCellRender = (column: keyof PoMonitoring, index: number, row: PoMonitoring) => {
        if (column === "projects") {
            if (row.projects.length) {
                const projectList = row.projects.map((project) => project.project);
                return (
                    <Box
                       display="flex"
                       flexDirection="row"
                       justifyContent="space-between"
                       alignItems="center"
                       sx={{
                           "&:hover .hover-icon": {
                               opacity: 1
                           }
                       }}
                    >
                        <Grid>
                            {sortListObj(projectList, "name").map((project, index) => (
                                <Chip key={project.id} size="small" style={{ marginRight: '5px', marginBottom: '5px' }} label={project.name}/>
                            ))}
                        </Grid>
                        <IconButton
                           className="hover-icon"
                           onClick={() => handleOpenEdit(row)}
                           sx={{
                              opacity: 0,
                              transition: 'opacity 0.3s'
                           }}
                        >
                            <Edit />
                        </IconButton>
                   </Box>
                )
            }
            return  <Button
                        onClick={() => {
                            setProjectsPoMonitoring([]);
                            setIdPoMonitoring(row.id);
                            setOpenProjectsDialog(true);
                        }}
                        color="secondary"
                        size="small">
                        <Add style={{fontSize: 'small'}}/>
                        {translate.t("add")}
                    </Button>
        }
        if (column.toLocaleLowerCase() === "status") {
            return (<PoMonitoringStatusComponent obj={row} />)
        }
        if (column.toLocaleLowerCase() === "category.name") {
            if (row.category) {
                return (
                   <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                          "&:hover .hover-icon": {
                              opacity: 1
                          }
                      }}
                   >
                       { row.category.name }
                       <IconButton
                          className="hover-icon"
                          onClick={() => handleOpenCategoryEdit(row)}
                          sx={{
                              opacity: 0,
                              transition: 'opacity 0.3s'
                          }}
                       >
                           <Edit />
                       </IconButton>
                   </Box>
                )
            }
            return  <Button
               onClick={() => {
                   setIdPoMonitoring(row.id);
                   setOpenCategoryDialog(true);
               }}
               color="secondary"
               size="small">
                <Add style={{fontSize: 'small'}}/>
                {translate.t("classify")}
            </Button>
        }
        if (column.toLocaleLowerCase() === "client.name") {
            if (row.client) {
                return (
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            "&:hover .hover-icon": {
                                opacity: 1
                            }
                        }}
                    >
                        { row.client.name }
                        <IconButton
                            className="hover-icon"
                            onClick={() => handleOpenClientEdit(row)}
                            sx={{
                                opacity: 0,
                                transition: 'opacity 0.3s'
                            }}
                        >
                            <Edit />
                        </IconButton>
                    </Box>
                )
            }
            return  <Button
                onClick={() => {
                    setIdPoMonitoring(row.id);
                    setOpenClientDialog(true);
                }}
                color="secondary"
                size="small">
                <Add style={{fontSize: 'small'}}/>
                {translate.t("classify")}
            </Button>
        }
        return row[column];
    }

    const orderByFn = (column: keyof PoMonitoring, direction: 'asc' | 'desc') => {
        const colunaIgual = column === filters.ordering?.replace("-", "");
        const dir = colunaIgual ? direction === 'asc' ? 'desc' : direction === 'desc' ? '' : 'asc' : 'asc';
        setOrderDirection(dir);
        updateFilterKeepingParam("ordering", !!dir ? `${dir === 'asc' ? '' : '-'}${column}` : '');
    }

    const goToDetail = (row: PoMonitoring) => {
        const url = `/purchase_order/purchase_order/${row.id}`;
        navigate(url)
    }

    const handleDateChange = (key: "start" | "end", date: any) => {
        console.log(date);
        if (date?.toString().includes("Invalid Date")) {
            setInvalidDate(true);
            return;
        }

        updateFilterKeepingParam(key, date ? ConvertDateToString(date) : "");
        key === "start" ? setStartDate(date) : setEndDate(date);

        const compareDate = new Date(filters[key === "start" ? "end" : "start"] + "T12:00:00");
        setInvalidDate(date && compareDate && (key === "start" ? date > compareDate : compareDate > date));
    };

    const handleStartDateChange = (date: any) => handleDateChange("start", date);
    const handleEndDateChange = (date: any) => handleDateChange("end", date);

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("po_monitoring")}
                </Typography>
            </div>
            <div className="floating-button" style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lastUpdated.toLowerCase().indexOf('erro') != -1 ? (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                ) : (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'green',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                )}
                <p className="text">{translate.t("last_updated")}: {lastUpdated}</p>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"} width="80%">
                                                <Grid >
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => updateFilterKeepingParam("client", event.target.value )}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid >
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("status")}
                                                        </InputLabel>
                                                        <Select
                                                            name="status"
                                                            label={translate.t("status")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.status || "0"}
                                                            onChange={(event) => updateFilterKeepingParam("status", event.target.value)}
                                                        >
                                                            {statusList?.map((stts: SelectItem) => (
                                                                <MenuItem key={stts.value} value={stts.value}>
                                                                    {translate.t(stts.label)}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid >
                                                    <Field
                                                        sx={{minWidth: "330px !important"}}
                                                        as={TextField}
                                                        name="search"
                                                        label={""}
                                                        fullWidth
                                                        variant={"outlined"}
                                                        autoFocus={true}
                                                        value={filters.name}
                                                        placeholder={translate.t("search_po_monitoring")}
                                                        onChange={(event: React.ChangeEvent<{
                                                            value: string
                                                        }>) => updateFilterKeepingParam("text", event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid >
                                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                        <DatePicker
                                                            label={translate.t("start_date")}
                                                            value={startDate}
                                                            onChange={handleStartDateChange}
                                                            inputRef={(props: any) => (
                                                                <TextField
                                                                    {...props}
                                                                    required={false}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    required: false,
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                                <Grid >
                                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                        <DatePicker
                                                            label={translate.t("end_date")}
                                                            value={endDate}
                                                            onChange={handleEndDateChange}
                                                            inputRef={(props: any) => (
                                                                <TextField
                                                                    {...props}
                                                                    required={false}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    required: false,
                                                                }
                                                            }}
                                                            minDate={new Date(filters.start + "T12:00:00") || null}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                                <Grid >
                                                    <Fab
                                                        disabled={invalidDate}
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            <Grid alignItems="center" container justifyContent="flex-end" spacing={3} width="20%">
                                                <Grid >
                                                    {loading && <CircularProgress size="30px" />}
                                                </Grid>
                                                <Grid >
                                                    <Button
                                                        disabled={loadingDownload}
                                                        color="secondary"
                                                        variant="contained"
                                                        startIcon={<Download />}
                                                        onClick={() => {
                                                            export_xlsx();
                                                        }}>{translate.t("export").toUpperCase()}
                                                        {loadingDownload && <CircularProgress size="20px" sx={{marginLeft: '10px'}} />}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Box alignItems="center" display="flex">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={filters.is_uncategorized === "true"}
                                                            onChange={(event) => {
                                                                updateFilterKeepingParam("is_uncategorized", event.target.checked ? "true" : "false");
                                                            }}
                                                        />
                                                    }
                                                    label={translate.t("po_uncategorized_filter")}
                                                />
                                            </Box>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Box sx={{marginTop: "16px", display: "flex", justifyContent: "space-between", gap: "20px"}}>
                <InfoCard title={dataSourceDashboard?.new_purchase_orders || 0}
                          description={translate.t("po_added")}
                          src={newpo} />
                <InfoCard title={dataSourceDashboard?.completed_purchase_orders || 0}
                          description={translate.t("po_delivered")}
                          src={deliveredpo} />
                <InfoCard title={dataSourceDashboard?.pending_purchase_orders || 0}
                          description={translate.t("po_in_partial_delivery")}
                          src={partialdelivered} />
            </Box>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["Client", "Origin", "PO", "PO Date", "Category", "Platform", "Status", "Duration", "action"]}
                    displayedColumns={["client.name", "origin", "number", "purchase_date", "category.name", "projects", "status", "lead_time", "action"]}
                    customColumns={["client.name", "projects", "status", "category.name"]}
                    orderByColumns={["client.name", "origin", "number", "purchase_date", "category.name"]}
                    dateColumns={["purchase_date"]}
                    orderBy={filters.ordering}
                    orderByFn={orderByFn}
                    orderDirection={orderDirection}
                    customCellRender={customCellRender}
                    handleDelete={handleDelete}
                    editWithDialog={handleOpenEdit}
                    url={url}
                    count={poMonitoringList?.count}
                    next={poMonitoringList?.next}
                    previous={poMonitoringList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    actionsComponent={TableActionComponent}
                    columnStyles={{1:"8%", 2:"3%", 3:"8%", 4:"8%", 5:"13%", 6:"23%", 7:"13%"}}
                    showActions={["detail"]}
                    rowsPerPageOptions={[10, 50, 100]}
                    goToDetail={goToDetail}
                    loading={loading}
                />
            </Paper>
            <AddProjectComponent
               openDialog={openProjectsDialog}
               id={idPoMonitoring}
               projects={projectsPoMonitoring}
               handleCloseDialog={(save: boolean) => {
                  if (save) { search(); }
                  setOpenProjectsDialog(false);
               }}
            />
            <AddCategoryComponent
               openDialog={openCategoryDialog}
               id={idPoMonitoring}
               category={categoryPoMonitoring}
               handleCloseDialog={(save: boolean) => {
                   if (save) { search(); }
                   setCategoryPoMonitoring(undefined);
                   setOpenCategoryDialog(false);
               }}
            />
            <AddClientComponent
                openDialog={openClientDialog}
                id={idPoMonitoring}
                client={clientPoMonitoring}
                handleCloseDialog={(save: boolean) => {
                    if (save) { search(); }
                    setClientPoMonitoring(undefined);
                    setOpenClientDialog(false);
                }}
            />
        </section>
    );
};

export default PoMonitoringComponent;
